import { z } from 'zod';

import { createIdbKvStore } from '#core/lib/storage/idbKvStore.ts';

/**
 * The single IndexedDB object store for all chat persistence: the thread index
 * (key {@link THREAD_LIST_KEY}, a `Record<remoteId, StoredThread>` of small
 * metadata) and each thread's full message tree (key {@link threadMessagesKey}).
 * The two key-spaces never collide, so one store holds both.
 *
 * ONE store, not two — idb-keyval's `createStore` creates exactly one object
 * store per database (its `upgradeneeded` runs once, for the store that opens
 * the DB first); a second `createStore` on the same DB would leave its store
 * non-existent, and every op on it would throw "not a known object store".
 *
 * IndexedDB — not localStorage — because a thread's branch tree (tool outputs
 * included) blows past localStorage's ~5 MB origin budget after a handful of
 * tool-heavy threads, at which point every `setItem` throws
 * `QuotaExceededError` and nothing persists.
 */
const chatStore = createIdbKvStore('atlas-chat', 'chat');

/** Key holding the thread index (the `Record<remoteId, StoredThread>` blob). */
const THREAD_LIST_KEY = 'threads';

/**
 * IndexedDB key for a single thread's persisted message history.
 *
 * SSOT for the `atlas-thread-messages:${id}` format: the history read/write
 * store and the thread-list `delete()` cleanup both route through this so a
 * delete can never orphan the blob its sibling wrote.
 */
export function threadMessagesKey(id: string): string {
  return `atlas-thread-messages:${id}`;
}

/** Thread metadata persisted to IndexedDB. */
export type StoredThread = {
  readonly status: 'regular' | 'archived';
  readonly remoteId: string;
  readonly externalId?: string | undefined;
  readonly title?: string | undefined;
};

/** Thread list response matching assistant-ui's RemoteThreadListResponse. */
export type StoredThreadListResponse = {
  threads: StoredThread[];
};

/** Thread initialize response. */
export type StoredThreadInitializeResponse = {
  remoteId: string;
  externalId: string;
};

const StoredThreadSchema = z.object({
  status: z.enum(['regular', 'archived']),
  remoteId: z.string(),
  externalId: z.string().optional(),
  title: z.string().optional(),
});

const ThreadsRecordSchema = z.record(z.string(), StoredThreadSchema);

/**
 * Thread index store backed by IndexedDB. Every method is async (IndexedDB
 * is); the read-modify-write on the small metadata record is fine here — the
 * index is tiny and mutated at human speed, unlike the message history whose
 * volume forced the single-writer atomic snapshot.
 */
export function createThreadListStore() {
  async function read(): Promise<Record<string, StoredThread>> {
    const parsed = ThreadsRecordSchema.safeParse(await chatStore.get<unknown>(THREAD_LIST_KEY));
    return parsed.success ? parsed.data : {};
  }

  async function getOrThrow(
    remoteId: string,
  ): Promise<{ thread: StoredThread; threads: Record<string, StoredThread> }> {
    const threads = await read();
    const thread = threads[remoteId];
    if (!thread) {
      throw new Error(`Thread not found: ${remoteId}`);
    }
    return { thread, threads };
  }

  return {
    async list(): Promise<StoredThreadListResponse> {
      return { threads: Object.values(await read()) };
    },

    async initialize(localId: string): Promise<StoredThreadInitializeResponse> {
      const threads = await read();
      threads[localId] = { status: 'regular', remoteId: localId, externalId: localId };
      await chatStore.set(THREAD_LIST_KEY, threads);
      return { remoteId: localId, externalId: localId };
    },

    async rename(remoteId: string, title: string): Promise<void> {
      const { thread, threads } = await getOrThrow(remoteId);
      threads[remoteId] = { ...thread, title };
      await chatStore.set(THREAD_LIST_KEY, threads);
    },

    async archive(remoteId: string): Promise<void> {
      const { thread, threads } = await getOrThrow(remoteId);
      threads[remoteId] = { ...thread, status: 'archived' };
      await chatStore.set(THREAD_LIST_KEY, threads);
    },

    async unarchive(remoteId: string): Promise<void> {
      const { thread, threads } = await getOrThrow(remoteId);
      threads[remoteId] = { ...thread, status: 'regular' };
      await chatStore.set(THREAD_LIST_KEY, threads);
    },

    async delete(remoteId: string): Promise<void> {
      const { threads } = await getOrThrow(remoteId);
      delete threads[remoteId];
      await chatStore.set(THREAD_LIST_KEY, threads);
      await deleteStoredThreadHistory(remoteId);
    },

    async fetch(remoteId: string): Promise<StoredThread> {
      return (await getOrThrow(remoteId)).thread;
    },
  };
}

/**
 * A single message storage entry persisted to thread history. Carries
 * the **full branch tree** (`parentId` links + `headId`) so it survives
 * a reload — `parentId` is the real branch parent supplied by
 * assistant-ui's message repository (mapped from its `MessageStorageEntry`
 * at the adapter boundary), not a synthesized linear-chain pointer.
 */
export type StoredHistoryEntry = {
  id: string;
  parentId: string | null;
  format: string;
  content: Record<string, unknown>;
};

/** The persisted thread-history document: the full branch tree. */
export type StoredHistory = {
  headId: string | null;
  entries: StoredHistoryEntry[];
};

/**
 * Per-entry parse aligned with assistant-ui's *actual* persisted-entry
 * contract — deliberately looser than a strict `z.string()`:
 *
 * - `MessageRepository.addOrUpdateMessage` treats ANY falsy `parentId`
 *   as a root message (`const prev = parentId ? … : null`), and a
 *   `parentId: undefined` write JSON-roundtrips to a *missing key*. So
 *   `parentId` absent / `undefined` / `''` ⇒ `null` (a root entry),
 *   never a parse failure.
 * - `format` is not load-bearing (the vendored `decode` ignores it);
 *   tolerate it missing.
 * - `content` must be an object to be a usable message; an entry that
 *   is not is dropped per-entry in `parseStoredHistoryDoc` — it can never
 *   take the rest of the conversation with it.
 *
 * A replayable document must never be rejected: this mirrors what
 * assistant-ui can import, not a stricter independent invariant.
 */
const StoredHistoryEntrySchema = z.object({
  id: z.string().min(1),
  parentId: z
    .string()
    .nullish()
    .transform((v) => (v ? v : null)),
  format: z.string().default(''),
  content: z.looseObject({}),
});

/**
 * The persisted-document shell. `entries` is parsed opaquely here and
 * salvaged element-by-element in `parseStoredHistoryDoc` so a single
 * malformed entry cannot discard the whole conversation. A document with no
 * `entries` array is not this schema (a flat shape, or corrupt) and loads as
 * empty, with no conversion.
 */
const StoredHistoryDocSchema = z.object({
  headId: z
    .string()
    .nullish()
    .transform((v) => (v ? v : null)),
  entries: z.array(z.unknown()),
});

/**
 * Per-entry salvage of a persisted history document: keep every entry
 * assistant-ui could replay, drop only the unusable ones, so one malformed
 * entry (a corrupt content blob) can never discard the whole conversation.
 * `normalizeStoredHistory` (called by `load`) then re-roots any link to a
 * dropped parent. A value that is not our document shape at all (including
 * `undefined` for an absent key) loads as the typed empty document.
 */
function parseStoredHistoryDoc(parsed: unknown): StoredHistory {
  const doc = StoredHistoryDocSchema.safeParse(parsed);
  if (!doc.success) {
    return { headId: null, entries: [] };
  }
  const entries = doc.data.entries.flatMap((candidate) => {
    const entry = StoredHistoryEntrySchema.safeParse(candidate);
    return entry.success ? [entry.data as StoredHistoryEntry] : [];
  });
  const dropped = doc.data.entries.length - entries.length;
  if (dropped > 0) {
    console.error('parseStoredHistoryDoc: dropped', dropped, 'unparseable history entries; kept', entries.length);
  }
  return { headId: doc.data.headId, entries };
}

/**
 * Normalize a persisted history document so it can always be replayed
 * by assistant-ui's `MessageRepository.import`, which walks entries in
 * array order and **throws** ("Parent message not found") if a child
 * precedes its parent — aborting the whole import and wiping the thread
 * on reload.
 *
 * Persisted append order is not reliable: branches interleave and the
 * debounced snapshot can encode a child before its parent. So at the load
 * boundary:
 *
 * 1. **Dangling-link repair** — an entry whose `parentId` references a
 *    missing entry is re-rooted (`parentId: null`) so one bad link
 *    cannot throw and wipe everything.
 * 2. **Stable topological order** — every parent precedes its children;
 *    original relative order is otherwise preserved (sibling/branch
 *    order, hence active-branch selection, is stable).
 * 3. **Head repair** — a `headId` that is absent or an *interior* node
 *    (a symptom of out-of-order writes, e.g. a cancelled-interrupt
 *    turn) is resolved down to the active leaf, descending and
 *    preferring the most-recently-appended child at a branch point. A
 *    `headId` that is already a valid leaf is kept as-is, so
 *    assistant-ui's branch selection after regenerate/edit is preserved.
 *
 * Pure and declarative; the `ancestors`/`visited` sets make a corrupt
 * cyclic document terminate instead of hanging.
 */
export function normalizeStoredHistory(doc: StoredHistory): StoredHistory {
  const ids = new Set(doc.entries.map((e: StoredHistoryEntry) => e.id));
  const entries = doc.entries.map((e: StoredHistoryEntry) =>
    e.parentId != null && !ids.has(e.parentId) ? { ...e, parentId: null } : e,
  );
  const byId = new Map(entries.map((e: StoredHistoryEntry) => [e.id, e] as const));

  const placeWithAncestry = (
    acc: readonly StoredHistoryEntry[],
    entry: StoredHistoryEntry,
    ancestors: ReadonlySet<string>,
  ): readonly StoredHistoryEntry[] => {
    if (acc.some((e: StoredHistoryEntry) => e.id === entry.id) || ancestors.has(entry.id)) {
      return acc;
    }
    const parent = entry.parentId == null ? undefined : byId.get(entry.parentId);
    const seeded = parent ? placeWithAncestry(acc, parent, new Set([...ancestors, entry.id])) : acc;
    return seeded.some((e: StoredHistoryEntry) => e.id === entry.id) ? seeded : [...seeded, entry];
  };
  const ordered = entries.reduce(
    (acc: readonly StoredHistoryEntry[], entry: StoredHistoryEntry) => placeWithAncestry(acc, entry, new Set()),
    [] as readonly StoredHistoryEntry[],
  );

  const parentIds = [...new Set(ordered.flatMap((e: StoredHistoryEntry) => (e.parentId == null ? [] : [e.parentId])))];
  const childrenOf = new Map(
    parentIds.map(
      (parentId: string) => [parentId, ordered.filter((e: StoredHistoryEntry) => e.parentId === parentId)] as const,
    ),
  );

  const leafFrom = (node: StoredHistoryEntry, visited: ReadonlySet<string>): string => {
    const next = childrenOf.get(node.id)?.at(-1);
    return !next || visited.has(next.id) ? node.id : leafFrom(next, new Set([...visited, node.id]));
  };
  const start = doc.headId == null ? undefined : byId.get(doc.headId);
  const headId = start ? leafFrom(start, new Set([start.id])) : (ordered.at(-1)?.id ?? null);

  return { headId, entries: [...ordered] };
}

/**
 * Remove a thread's persisted history from IndexedDB. SSOT for history
 * teardown so a thread delete can never orphan the message tree.
 */
export async function deleteStoredThreadHistory(threadId: string): Promise<void> {
  await chatStore.del(threadMessagesKey(threadId));
}

/**
 * Per-thread history store backed by IndexedDB. `load` is async (IndexedDB
 * is) and `save` replaces the whole document atomically — the caller
 * ({@link createThreadHistoryAdapter}'s snapshot) supplies the full
 * authoritative branch tree, so this is deliberately NOT a read-modify-write
 * and a failed or divergent read can never feed back into a write.
 */
export function createThreadHistoryStore(threadId: string) {
  const key = threadMessagesKey(threadId);

  return {
    async load(): Promise<StoredHistory> {
      // Normalize on the way out so a reload's `thread.import` always sees a
      // replayable DAG (parent-before-child, valid head), whatever entry order
      // or head the on-disk document carries. An absent key parses to empty.
      return normalizeStoredHistory(parseStoredHistoryDoc(await chatStore.get<unknown>(key)));
    },

    async save(doc: StoredHistory): Promise<void> {
      await chatStore.set(key, doc);
    },
  };
}
